import { type FC, useState } from 'react';

import type { ActionItemVm } from '@eluve/api-contract';
import { allRowsConfirmed, itemReviewPosture } from '@eluve/api-contract';
import { Button, Icon, Input, Stack, Text } from '@eluve/components';

import { ItemEditForm } from './ItemEditForm';
import { ItemReviewRow } from './ItemReviewRow';
import { PendingChangeDiff } from './PendingChangeDiff';
import { SectionBulkControls } from './SectionBulkControls';
import { changeByField, confirmableRows } from './proposal-rows';

const ActionItemEditForm: FC<{
  initial: string;
  disabled?: boolean;
  onCancel: () => void;
  onSave: (text: string) => void;
  onDelete?: () => void;
}> = ({ initial, disabled, onCancel, onSave, onDelete }) => {
  const [text, setText] = useState(initial);
  const isEdit = Boolean(onDelete);

  return (
    <ItemEditForm
      title={isEdit ? 'Edit Action Item' : 'Add Action Item'}
      saveLabel={isEdit ? 'Save Changes' : 'Save'}
      saveDisabled={text.trim().length === 0}
      disabled={disabled}
      onCancel={onCancel}
      onSave={() => onSave(text.trim())}
      onDelete={onDelete}
      deleteLabel="Delete Action Item"
      deleteConfirmTitle="Delete this action item?"
      deleteConfirmBody="This removes the action item from the plan. You can’t undo a delete."
    >
      <Input
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        placeholder="e.g. Chin tucks — 3×10 daily"
        aria-label="Action item text"
        className="mt-3"
      />
    </ItemEditForm>
  );
};

export const ActionItemsSection: FC<{
  actionItems: ActionItemVm[];
  disabled?: boolean;
  showBulkUndo?: boolean;
  onConfirm: (item: ActionItemVm) => void;
  onConfirmAll?: (items: ActionItemVm[]) => void;
  onUndoConfirmAll?: () => void;
  onReject: (item: ActionItemVm) => void;
  onUndo: (item: ActionItemVm) => void;
  onEditText: (item: ActionItemVm, text: string) => void;
  onDelete: (item: ActionItemVm) => void;
  onAdd: (text: string) => void;
}> = ({
  actionItems,
  disabled,
  showBulkUndo,
  onConfirm,
  onConfirmAll,
  onUndoConfirmAll,
  onReject,
  onUndo,
  onEditText,
  onDelete,
  onAdd,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const confirmable = confirmableRows(actionItems);
  const allConfirmed = allRowsConfirmed(actionItems.map(itemReviewPosture));

  return (
    <section className="w-full rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
      <SectionBulkControls
        title="Action Items"
        confirmableCount={confirmable.length}
        allConfirmed={allConfirmed}
        showUndoAll={Boolean(showBulkUndo)}
        disabled={disabled}
        onConfirmAll={() =>
          onConfirmAll
            ? onConfirmAll(confirmable)
            : confirmable.forEach(onConfirm)
        }
        onUndoAll={onUndoConfirmAll}
      />

      {actionItems.length === 0 && !adding ? (
        <Text size="sm" color="secondary" className="mt-3 block">
          No action items yet — exercises land here once prescribed.
        </Text>
      ) : (
        <Stack direction="vertical" gap={2} className="mt-3">
          {actionItems.map((item) => {
            const textChange = changeByField(item, 'text');
            return editingId === item.id ? (
              <ActionItemEditForm
                key={item.id}
                initial={item.text}
                disabled={disabled}
                onCancel={() => setEditingId(null)}
                onSave={(text) => {
                  onEditText(item, text);
                  setEditingId(null);
                }}
                onDelete={() => {
                  onDelete(item);
                  setEditingId(null);
                }}
              />
            ) : (
              <ItemReviewRow
                key={item.id}
                itemState={item.itemState}
                decision={item.decision}
                title={textChange ? undefined : item.text}
                disabled={disabled}
                onConfirm={() => onConfirm(item)}
                onReject={() => onReject(item)}
                onUndo={() => onUndo(item)}
                onEdit={() => setEditingId(item.id)}
              >
                {textChange && <PendingChangeDiff change={textChange} />}
              </ItemReviewRow>
            );
          })}
          {adding && (
            <ActionItemEditForm
              initial=""
              disabled={disabled}
              onCancel={() => setAdding(false)}
              onSave={(text) => {
                onAdd(text);
                setAdding(false);
              }}
            />
          )}
        </Stack>
      )}
      {!adding && (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => setAdding(true)}
          className="mt-3"
        >
          <Icon name="Plus" size="auto" />
          Add Action Item
        </Button>
      )}
    </section>
  );
};
